<script lang="ts">
  import { createEventDispatcher, onMount } from "svelte";
  import { project } from "$lib/services/store";
  import Group from "$lib/admin/components/Group.svelte";
  import { 
    Card, 
    Button, 
    Checkbox, 
    Spinner, 
    Alert,
    Badge,
    Input
  } from "flowbite-svelte";
  import Icon from "@iconify/svelte";
  import dbHttp from "$lib/services/dbHttp";
  import { getBannerImage } from "$lib/common/helpers/image";
  import type { Banner } from "@/src/types/banners/Banner";
  
  interface BannerProps {
    bannerIds: number[];
  }
  
  export let props: BannerProps | null = null;
  
  if (!props) {
    props = { 
      bannerIds: []
    };
  }

  const dispatch = createEventDispatcher<{keyup: void}>();
  
  let isLoading = false;
  let loadError: string | null = null;
  let allBanners: Banner[] = [];
  let searchTerm = "";
  let draggedBanner: Banner | null = null;
  
  $: filteredBanners = searchTerm 
    ? allBanners.filter(banner => 
        (banner.title && banner.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (banner.title_internal && banner.title_internal.toLowerCase().includes(searchTerm.toLowerCase())) ||
        banner.id.toString().includes(searchTerm)
      )
    : allBanners;
  
  $: selectedBanners = props && props.bannerIds && props.bannerIds.length > 0 
    ? props.bannerIds
        .map(id => allBanners.find(banner => banner.id === id))
        .filter((banner): banner is Banner => banner !== undefined)
    : [];
  
  onMount(async () => {
    await loadBanners();
  });
  
  async function loadBanners() {
    isLoading = true;
    loadError = null;
    
    try {
      const { data, error } = await dbHttp.query('banners', {
        select: '*',
        order: 'created_at.desc'
      });
      
      if (error) {
        throw new Error(typeof error === 'string' ? error : 'Failed to fetch banners');
      }
      
      if (data) {
        allBanners = data as Banner[];
      } else {
        allBanners = [];
      }
    } catch (error: unknown) {
      console.error("Error loading banners:", error);
      loadError = error instanceof Error ? error.message : 'Failed to load banners';
    } finally {
      isLoading = false;
    }
  }
  
  function toggleBanner(bannerId: number) {
    if (!props) return;
    if (!props.bannerIds) {
      props.bannerIds = [];
    }
    
    if (props.bannerIds.includes(bannerId)) {
      props.bannerIds = props.bannerIds.filter(id => id !== bannerId);
    } else {
      props.bannerIds = [...props.bannerIds, bannerId];
    }
    dispatch("keyup");
  }

  function handleDragStart(banner: Banner) {
    draggedBanner = banner;
  }

  function handleDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.classList.add('bg-gray-100', 'dark:bg-gray-700');
    }
  }

  function handleDragLeave(event: DragEvent) {
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.classList.remove('bg-gray-100', 'dark:bg-gray-700');
    }
  }

  function handleDrop(event: DragEvent, targetBanner: Banner) {
    event.preventDefault();
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.classList.remove('bg-gray-100', 'dark:bg-gray-700');
    }
    
    if (!draggedBanner || !props || !props.bannerIds) return;
    
    const newOrder = [...props.bannerIds];
    const draggedIndex = newOrder.indexOf(draggedBanner.id);
    const targetIndex = newOrder.indexOf(targetBanner.id);
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedBanner.id);
      
      props.bannerIds = newOrder;
      dispatch("keyup");
    }
    
    draggedBanner = null;
  }
</script>

<Group addClass="form-item">
  <div class="mb-4">
    <fieldset>
      <legend class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
        Select Banners
      </legend>
      
      <div class="mb-4">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Icon icon="heroicons:magnifying-glass" class="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </div>
          <Input 
            type="text" 
            class="pl-10" 
            placeholder="Search banners..." 
            bind:value={searchTerm}
          />
        </div>
      </div>
      
      {#if isLoading}
        <div class="flex justify-center items-center py-8">
          <Spinner size="8" />
          <span class="ml-2">Loading banners...</span>
        </div>
      {:else if loadError}
        <Alert color="red" class="mb-4">
          <span class="font-medium">Error!</span> {loadError}
          <Button size="xs" color="red" class="ml-2" on:click={loadBanners}>Retry</Button>
        </Alert>
      {:else if filteredBanners.length === 0}
        <div class="p-4 text-center text-gray-500 border border-gray-200 rounded-lg dark:border-gray-700 dark:text-gray-400">
          No banners found
        </div>
      {:else}
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          {#each filteredBanners.filter(banner => banner.status) as banner}
            <Card 
              padding="sm" 
              class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors {props && props.bannerIds && props.bannerIds.includes(banner.id) ? 'border-blue-500 dark:border-blue-500 ring-2 ring-blue-500' : ''}"
              on:click={() => toggleBanner(banner.id)}
            >
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 w-24 h-16 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
                  {#if banner.image}
                    <img 
                      src={getBannerImage(banner.image)} 
                      alt={banner.title} 
                      class="w-full h-full object-cover" 
                    />
                  {:else}
                    <div class="w-full h-full flex items-center justify-center">
                      <Icon icon="heroicons:photo" class="w-6 h-6 text-gray-400" />
                    </div>
                  {/if}
                </div>
                <div class="flex-grow">
                  {#if banner.title_internal}
                    <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {banner.title_internal}
                    </div>
                  {/if}
                  <h5 class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                    {banner.title}
                  </h5>
                  <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                    {banner.badge_text || 'No description'}
                  </p>
                  <div class="flex items-center mt-2">
                    <Badge color={banner.status ? "green" : "dark"} class="text-xs">
                      {banner.status ? "Active" : "Inactive"}
                    </Badge>
                    <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      ID: {banner.id}
                    </span>
                  </div>
                </div>
                <div class="flex-shrink-0">
                  <Checkbox checked={props && props.bannerIds && props.bannerIds.includes(banner.id)} />
                </div>
              </div>
            </Card>
          {/each}
        </div>
      {/if}
    </fieldset>
  </div>
  
  {#if selectedBanners.length > 0}
    <div class="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <h6 class="text-sm font-semibold mb-2 text-gray-900 dark:text-white">
        Selected Banners ({selectedBanners.length}) - Drag to reorder
      </h6>
      <div class="flex flex-col gap-2">
        {#each selectedBanners as banner}
          <div 
            class="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 cursor-move hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            draggable={true}
            role="listitem"
            aria-label="Draggable banner item"
            on:dragstart={() => handleDragStart(banner)}
            on:dragover={handleDragOver}
            on:dragleave={handleDragLeave}
            on:drop={(e) => handleDrop(e, banner)}
          >
            <Icon icon="heroicons:bars-2" class="w-4 h-4 text-gray-400" />
            <div class="flex-grow">
              {#if banner.title_internal}
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {banner.title_internal}
                </div>
              {/if}
              <div class="text-sm font-medium text-gray-900 dark:text-white">
                {banner.title}
              </div>
            </div>
            <button 
              type="button" 
              class="p-1 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
              on:click={() => toggleBanner(banner.id)}
            >
              <Icon icon="heroicons:x-mark" class="w-4 h-4" />
              <span class="sr-only">Remove {banner.title}</span>
            </button>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</Group>